import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  House,
  BookOpen,
  GraduationCap,
  ChartBar,
  ChartLine,
  Users,
  Stack,
  SignOut,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const learnerLinks = [
  { to: "/dashboard", label: "My Learning", icon: House, testid: "nav-my-learning" },
  { to: "/catalog", label: "Course Catalog", icon: BookOpen, testid: "nav-catalog" },
  { to: "/my-stats", label: "My Stats", icon: ChartLine, testid: "nav-my-stats" },
];

const adminLinks = [
  { to: "/admin", label: "Overview", icon: ChartBar, testid: "nav-admin-overview" },
  { to: "/admin/analytics", label: "Analytics", icon: ChartLine, testid: "nav-admin-analytics" },
  { to: "/admin/courses", label: "Manage Courses", icon: Stack, testid: "nav-admin-courses" },
  { to: "/admin/users", label: "Manage Users", icon: Users, testid: "nav-admin-users" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const item = (link) => (
    <NavLink
      key={link.to}
      to={link.to}
      end={link.to === "/admin"}
      data-testid={link.testid}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 mx-3 px-3 py-2.5 rounded-lg transition-colors",
          isActive
            ? "bg-[#E25822] text-white"
            : "text-[#B5B5AE] hover:text-white hover:bg-white/[0.06]",
        ].join(" ")
      }
    >
      <link.icon size={18} weight="regular" />
      <span className="text-sm font-medium">{link.label}</span>
    </NavLink>
  );

  return (
    <aside
      className="hidden lg:flex w-64 shrink-0 flex-col bg-[#161614] sticky top-0 h-screen"
      data-testid="app-sidebar"
    >
      <div className="px-5 py-6 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#E25822] flex items-center justify-center">
          <GraduationCap size={20} weight="bold" color="#FFFFFF" />
        </div>
        <div>
          <div className="font-heading text-base font-semibold leading-none tracking-tight text-white">
            UzviS Learning
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A8A85] mt-1.5">
            Uzvi Services
          </div>
        </div>
      </div>

      <div className="px-5 mt-2 mb-4">
        <div className="h-px bg-white/[0.06]"></div>
      </div>

      <nav className="flex-1 overflow-y-auto pb-4 space-y-0.5">
        <div className="px-6 mb-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#5C5C58]">
          Learner
        </div>
        {learnerLinks.map(item)}

        {user?.role === "admin" && (
          <>
            <div className="px-6 mt-6 mb-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#5C5C58]">
              Administration
            </div>
            {adminLinks.map(item)}
          </>
        )}
      </nav>

      <div className="p-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3 mb-3 px-1" data-testid="sidebar-user">
          <div className="w-9 h-9 rounded-full bg-[#E25822]/20 text-[#E25822] flex items-center justify-center font-heading font-semibold text-sm">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-[#8A8A85] truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="sidebar-logout"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#B5B5AE] border border-white/[0.08] hover:border-white/[0.18] hover:text-white transition-colors"
        >
          <SignOut size={15} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
